import React, { useEffect, useState, useRef } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import { toast } from "sonner";
import { Upload, FileText, FileSpreadsheet, Download, CheckCircle2, XCircle, Trash2, AlertCircle, Sparkles, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import api from "../../lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const DOC_TYPES = [
  { value: "technical_sheet", label: "Scheda tecnica" },
  { value: "coa", label: "COA (Certificato Analisi)" },
  { value: "certificate_origin", label: "Certificato d'Origine" },
  { value: "label_artwork", label: "Label artwork" },
  { value: "export_certificate", label: "Certificato Export" },
  { value: "catalog", label: "Catalogo" },
  { value: "other", label: "Altro" },
];

const ENTITY_TYPES = [
  { value: "wine", label: "Vino" },
  { value: "lot", label: "Lotto B2B" },
  { value: "supplier", label: "Fornitore (utente B2B)" },
];

function StatusPill({ ok, error }) {
  if (error > 0 && ok === 0) return <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-red-100 text-red-700 rounded-sm"><XCircle className="h-3 w-3" /> Errore</span>;
  if (error > 0) return <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700 rounded-sm"><AlertCircle className="h-3 w-3" /> Parziale</span>;
  return <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-green-100 text-green-700 rounded-sm"><CheckCircle2 className="h-3 w-3" /> OK</span>;
}

function CsvUploader({ kind, label, hint }) {
  const [file, setFile] = useState(null);
  const [dryRun, setDryRun] = useState(true);
  const [sellerEmail, setSellerEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const downloadTemplate = async () => {
    try {
      const r = await api.get(`/admin/imports/templates/${kind}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `template_${kind}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Errore download template");
    }
  };

  const submit = async () => {
    if (!file) { toast.error("Seleziona un file CSV"); return; }
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("dry_run", String(dryRun));
      if (kind === "lots" && sellerEmail) fd.append("seller_email", sellerEmail);
      const r = await api.post(`/admin/imports/${kind}/csv`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(r.data);
      if (r.data.rows_error === 0) {
        toast.success(`${dryRun ? "[Anteprima] " : ""}${r.data.rows_ok}/${r.data.rows_total} righe processate`);
      } else {
        toast.warning(`${r.data.rows_ok} OK · ${r.data.rows_error} errori`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore upload");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="bg-card border border-border rounded-sm p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-[hsl(var(--primary))] font-bold mb-1">{label}</div>
            <p className="text-sm text-muted-foreground max-w-xl">{hint}</p>
          </div>
          <Button data-testid={`btn-template-${kind}`} variant="outline" size="sm" onClick={downloadTemplate} className="gap-2">
            <Download className="h-3.5 w-3.5" /> Template CSV
          </Button>
        </div>

        <div className="mt-5 grid sm:grid-cols-[1fr_auto_auto] gap-3 items-end">
          <div>
            <Label className="text-xs uppercase tracking-wider font-semibold">File CSV</Label>
            <Input
              data-testid={`input-file-${kind}`}
              ref={inputRef}
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }}
              className="mt-1.5"
            />
          </div>
          <label className="flex items-center gap-2 text-xs cursor-pointer pb-2">
            <input data-testid={`chk-dry-${kind}`} type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} className="accent-[hsl(var(--primary))]" />
            Anteprima (no scrittura)
          </label>
          <Button
            data-testid={`btn-upload-${kind}`}
            onClick={submit}
            disabled={busy || !file}
            className="bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90 gap-2"
          >
            <Upload className="h-4 w-4" /> {busy ? "Caricamento…" : (dryRun ? "Anteprima" : "Importa")}
          </Button>
        </div>

        {kind === "lots" && (
          <div className="mt-3">
            <Label className="text-xs uppercase tracking-wider font-semibold">Seller email (opzionale)</Label>
            <Input
              data-testid="input-seller-email"
              type="email"
              placeholder="rivenditore@example.com — se vuoto: admin"
              value={sellerEmail}
              onChange={(e) => setSellerEmail(e.target.value)}
              className="mt-1.5 max-w-md"
            />
            <p className="text-[10px] text-muted-foreground mt-1">Se la colonna <code>seller_email</code> è popolata nel CSV, questa viene usata come default per le righe vuote.</p>
          </div>
        )}
      </div>

      {result && (
        <div data-testid={`result-${kind}`} className="bg-card border border-border rounded-sm p-5 space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <StatusPill ok={result.rows_ok} error={result.rows_error} />
            {result.dry_run && <span className="chip bg-amber-50 text-amber-700">Anteprima · nessuna scrittura</span>}
            <span className="text-sm text-muted-foreground"><strong>{result.rows_ok}</strong> OK su <strong>{result.rows_total}</strong> · {result.inserted} inseriti · {result.updated} aggiornati · {result.rows_error} errori</span>
          </div>
          {result.errors?.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-wider font-semibold text-red-700 mb-2">Errori (prime {result.errors.length} righe)</div>
              <div className="max-h-72 overflow-auto border border-red-200 rounded-sm bg-red-50/40">
                <table className="w-full text-xs">
                  <thead className="bg-red-100 text-red-900">
                    <tr><th className="text-left p-2">Riga</th><th className="text-left p-2">Errore</th><th className="text-left p-2">Dati</th></tr>
                  </thead>
                  <tbody>
                    {result.errors.map((er, i) => (
                      <tr key={i} className="border-t border-red-100">
                        <td className="p-2 font-mono">{er.row}</td>
                        <td className="p-2 text-red-700">{er.error}</td>
                        <td className="p-2 font-mono text-[10px] text-muted-foreground truncate max-w-md">{JSON.stringify(er.data)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DocumentUploader() {
  const [entityType, setEntityType] = useState("wine");
  const [entityId, setEntityId] = useState("");
  const [docType, setDocType] = useState("technical_sheet");
  const [docLabel, setDocLabel] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [docs, setDocs] = useState([]);
  const [filter, setFilter] = useState({ entity_type: "", doc_type: "" });
  const [scanState, setScanState] = useState({ docId: null, busy: false, result: null, error: null, saveToWine: true });

  const fetchDocs = async () => {
    try {
      const params = {};
      if (filter.entity_type) params.entity_type = filter.entity_type;
      if (filter.doc_type) params.doc_type = filter.doc_type;
      const r = await api.get("/admin/imports/documents", { params });
      setDocs(r.data.documents || []);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { fetchDocs(); /* eslint-disable-next-line */ }, [filter.entity_type, filter.doc_type]);

  const submit = async () => {
    if (!file) { toast.error("Seleziona un file PDF"); return; }
    if (!entityId) { toast.error("Indica l'ID entità (vino / lotto / fornitore)"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("entity_type", entityType);
      fd.append("entity_id", entityId);
      fd.append("doc_type", docType);
      if (docLabel) fd.append("label", docLabel);
      await api.post("/admin/imports/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Documento caricato");
      setFile(null); setDocLabel("");
      fetchDocs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore upload");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare il documento?")) return;
    try {
      await api.delete(`/admin/imports/documents/${id}`);
      toast.success("Documento eliminato");
      fetchDocs();
    } catch (e) {
      toast.error("Errore eliminazione");
    }
  };

  const fmtSize = (b) => b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(2)} MB` : `${(b / 1024).toFixed(0)} KB`;
  const fmtDate = (iso) => new Date(iso).toLocaleString("it-IT");

  const runScan = async (doc) => {
    setScanState({ docId: doc.id, busy: true, result: null, error: null, saveToWine: doc.entity_type === "wine" });
    try {
      const r = await api.post(`/admin/imports/documents/${doc.id}/scan?save_to_wine=${doc.entity_type === "wine"}`);
      setScanState((s) => ({ ...s, busy: false, result: r.data }));
      toast.success(`Scan completato · confidence ${(r.data.extracted?.confidence ?? 0).toFixed(2)}`);
      fetchDocs();
    } catch (e) {
      setScanState((s) => ({ ...s, busy: false, error: e.response?.data?.detail || "Errore scan" }));
      toast.error(e.response?.data?.detail || "Errore scan");
    }
  };

  const closeScan = () => setScanState({ docId: null, busy: false, result: null, error: null, saveToWine: true });

  return (
    <div className="space-y-5">
      <div className="bg-card border border-border rounded-sm p-5">
        <div className="text-xs uppercase tracking-[0.25em] text-[hsl(var(--primary))] font-bold mb-1">Carica documento PDF</div>
        <p className="text-sm text-muted-foreground max-w-xl">Schede tecniche, COA, certificati origine ed export, label artwork. Massimo 25 MB. Il documento viene legato all'entità (vino / lotto / fornitore).</p>

        <div className="mt-5 grid md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-wider font-semibold">Tipo entità</Label>
            <Select value={entityType} onValueChange={setEntityType}>
              <SelectTrigger data-testid="doc-entity-type" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ENTITY_TYPES.map((e) => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider font-semibold">ID entità</Label>
            <Input
              data-testid="doc-entity-id"
              placeholder="es. c7c8fa70..."
              value={entityId}
              onChange={(e) => setEntityId(e.target.value)}
              className="mt-1.5"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider font-semibold">Tipo documento</Label>
            <Select value={docType} onValueChange={setDocType}>
              <SelectTrigger data-testid="doc-type" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DOC_TYPES.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider font-semibold">Etichetta (opzionale)</Label>
            <Input
              data-testid="doc-label"
              placeholder="es. Brunello 2018 - Scheda tecnica IT"
              value={docLabel}
              onChange={(e) => setDocLabel(e.target.value)}
              className="mt-1.5"
            />
          </div>
        </div>

        <div className="mt-4 flex items-end gap-3 flex-wrap">
          <div className="flex-1 min-w-[280px]">
            <Label className="text-xs uppercase tracking-wider font-semibold">PDF</Label>
            <Input
              data-testid="doc-file"
              type="file"
              accept="application/pdf"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="mt-1.5"
            />
          </div>
          <Button
            data-testid="btn-doc-upload"
            onClick={submit}
            disabled={busy || !file}
            className="bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90 gap-2"
          >
            <Upload className="h-4 w-4" /> {busy ? "Caricamento…" : "Carica PDF"}
          </Button>
        </div>
      </div>

      <div className="bg-card border border-border rounded-sm p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <div className="text-xs uppercase tracking-[0.25em] text-[hsl(var(--primary))] font-bold">Document repository · {docs.length} file</div>
          <div className="flex gap-2">
            <Select value={filter.entity_type || "all"} onValueChange={(v) => setFilter({ ...filter, entity_type: v === "all" ? "" : v })}>
              <SelectTrigger data-testid="filter-doc-entity" className="h-8 text-xs w-[140px]"><SelectValue placeholder="Tipo entità" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                {ENTITY_TYPES.map((e) => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filter.doc_type || "all"} onValueChange={(v) => setFilter({ ...filter, doc_type: v === "all" ? "" : v })}>
              <SelectTrigger data-testid="filter-doc-type" className="h-8 text-xs w-[200px]"><SelectValue placeholder="Tipo doc" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                {DOC_TYPES.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        {docs.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center">Nessun documento caricato.</div>
        ) : (
          <div className="overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-[hsl(var(--muted))] text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left p-2">File</th>
                  <th className="text-left p-2">Tipo</th>
                  <th className="text-left p-2">Entità</th>
                  <th className="text-left p-2">Dimensione</th>
                  <th className="text-left p-2">Caricato</th>
                  <th className="text-right p-2">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {docs.map((d) => (
                  <tr key={d.id} data-testid={`doc-row-${d.id}`} className="border-b border-border">
                    <td className="p-2 font-medium flex items-center gap-2">
                      <FileText className="h-4 w-4 text-[hsl(var(--primary))]" />
                      <span className="truncate max-w-[280px]">{d.label}</span>
                    </td>
                    <td className="p-2 text-xs"><span className="chip bg-[hsl(var(--muted))]">{d.doc_type}</span></td>
                    <td className="p-2 text-xs">{d.entity_type} · <code className="text-[10px]">{d.entity_id.slice(0, 8)}</code></td>
                    <td className="p-2 text-xs text-muted-foreground">{fmtSize(d.size_bytes)}</td>
                    <td className="p-2 text-xs text-muted-foreground">{fmtDate(d.uploaded_at)}</td>
                    <td className="p-2 text-right">
                      <a
                        data-testid={`doc-view-${d.id}`}
                        href={`${BACKEND}${d.stored_path}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-[hsl(var(--primary))] underline mr-3"
                      >Apri</a>
                      <button
                        data-testid={`doc-scan-${d.id}`}
                        onClick={() => runScan(d)}
                        disabled={scanState.busy && scanState.docId === d.id}
                        className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))] hover:text-white rounded-sm font-semibold uppercase tracking-wider mr-2 disabled:opacity-50"
                        title="Estrai campi MAPA con AI"
                      >
                        {scanState.busy && scanState.docId === d.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                        Scan AI
                      </button>
                      {d.mapa_scanned_at && <span className="chip bg-green-50 text-green-700 mr-2 text-[10px]">scanned</span>}
                      <button
                        data-testid={`doc-delete-${d.id}`}
                        onClick={() => remove(d.id)}
                        className="text-red-600 hover:text-red-800"
                      ><Trash2 className="h-4 w-4 inline" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={!!scanState.docId} onOpenChange={(open) => { if (!open) closeScan(); }}>
        <DialogContent data-testid="scan-modal" className="max-w-2xl max-h-[85vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 editorial-heading">
              <Sparkles className="h-5 w-5 text-[hsl(var(--primary))]" />
              Estrazione AI · Campi MAPA
            </DialogTitle>
            <DialogDescription>
              Lettura del PDF tramite pypdf e analisi GPT-5.2: estrazione dei 21 campi MAPA-compliance per il Brasile.
            </DialogDescription>
          </DialogHeader>
          {scanState.busy && (
            <div className="py-12 text-center text-sm text-muted-foreground">
              <Loader2 className="h-8 w-8 animate-spin mx-auto mb-3 text-[hsl(var(--primary))]" />
              Lettura del PDF e analisi GPT-5.2 in corso…
            </div>
          )}
          {scanState.error && (
            <div className="py-6 text-sm text-red-700 bg-red-50 p-3 rounded-sm">
              <strong>Errore:</strong> {scanState.error}
            </div>
          )}
          {scanState.result && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 flex-wrap text-xs">
                <span className="chip bg-green-50 text-green-700">
                  Confidence {((scanState.result.extracted?.confidence ?? 0) * 100).toFixed(0)}%
                </span>
                <span className="text-muted-foreground">{scanState.result.text_chars} caratteri analizzati</span>
                {scanState.result.saved_to_wine && <span className="chip bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]">Salvato sul vino collegato</span>}
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                {Object.entries(scanState.result.extracted || {})
                  .filter(([k]) => !k.startsWith("_"))
                  .map(([k, v]) => (
                    <div key={k} className="border border-border rounded-sm p-2 bg-[hsl(var(--muted))]/30">
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{k.replace(/_/g, " ")}</div>
                      <div className="font-medium mt-0.5 break-words">
                        {v === null || v === undefined ? <span className="text-muted-foreground italic">—</span> :
                          Array.isArray(v) ? v.join(", ") :
                            typeof v === "object" ? JSON.stringify(v) :
                              String(v)}
                      </div>
                    </div>
                  ))}
              </div>
              <details className="text-xs">
                <summary className="cursor-pointer text-muted-foreground hover:text-foreground">JSON completo</summary>
                <pre className="mt-2 p-3 bg-[hsl(var(--muted))] rounded-sm overflow-auto max-h-60 text-[10px]">{JSON.stringify(scanState.result.extracted, null, 2)}</pre>
              </details>
            </div>
          )}
          <DialogFooter>
            <Button data-testid="scan-close" onClick={closeScan} variant="outline">Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function JobsHistory() {
  const [jobs, setJobs] = useState([]);
  const fetch_ = async () => {
    try {
      const r = await api.get("/admin/imports/jobs?limit=50");
      setJobs(r.data.jobs || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { fetch_(); }, []);

  const fmtDate = (iso) => new Date(iso).toLocaleString("it-IT");

  return (
    <div className="bg-card border border-border rounded-sm p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs uppercase tracking-[0.25em] text-[hsl(var(--primary))] font-bold">Storico job · {jobs.length}</div>
        <Button variant="outline" size="sm" onClick={fetch_}>Aggiorna</Button>
      </div>
      {jobs.length === 0 ? (
        <div className="text-sm text-muted-foreground py-6 text-center">Nessun job ancora.</div>
      ) : (
        <div className="overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-[hsl(var(--muted))] text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left p-2">Quando</th>
                <th className="text-left p-2">Tipo</th>
                <th className="text-left p-2">File</th>
                <th className="text-left p-2">Stato</th>
                <th className="text-left p-2">Righe</th>
                <th className="text-left p-2">Inserite</th>
                <th className="text-left p-2">Aggiornate</th>
                <th className="text-left p-2">Errori</th>
                <th className="text-left p-2">Da</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((j) => (
                <tr key={j.id} data-testid={`job-row-${j.id}`} className="border-b border-border">
                  <td className="p-2 text-xs text-muted-foreground">{fmtDate(j.created_at)}</td>
                  <td className="p-2 text-xs">{j.type}</td>
                  <td className="p-2 text-xs truncate max-w-[200px]">{j.filename}</td>
                  <td className="p-2"><StatusPill ok={j.rows_ok} error={j.rows_error} /> {j.dry_run && <span className="chip bg-amber-50 text-amber-700 ml-1">dry</span>}</td>
                  <td className="p-2 text-xs">{j.rows_total}</td>
                  <td className="p-2 text-xs text-green-700 font-semibold">{j.inserted}</td>
                  <td className="p-2 text-xs">{j.updated}</td>
                  <td className="p-2 text-xs text-red-700">{j.rows_error}</td>
                  <td className="p-2 text-xs text-muted-foreground">{j.created_by}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function AdminImports() {
  return (
    <div data-testid="admin-imports-page" className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.3em] text-[hsl(var(--primary))] font-semibold mb-2">B2B · Import & Documenti</div>
        <h1 className="editorial-heading text-4xl md:text-5xl">Catalogo & Compliance Hub</h1>
        <p className="text-muted-foreground mt-2 max-w-2xl">Importa vini e lotti B2B da CSV, carica i documenti PDF (schede tecniche, COA, certificati origine, label, certificati export) e tieni traccia di tutti i job.</p>
      </div>

      <Tabs defaultValue="wines">
        <TabsList>
          <TabsTrigger data-testid="tab-wines" value="wines" className="gap-2"><FileSpreadsheet className="h-4 w-4" /> Vini CSV</TabsTrigger>
          <TabsTrigger data-testid="tab-lots" value="lots" className="gap-2"><FileSpreadsheet className="h-4 w-4" /> Lotti B2B CSV</TabsTrigger>
          <TabsTrigger data-testid="tab-docs" value="docs" className="gap-2"><FileText className="h-4 w-4" /> Documenti PDF</TabsTrigger>
          <TabsTrigger data-testid="tab-jobs" value="jobs">Storico</TabsTrigger>
        </TabsList>
        <TabsContent value="wines" className="mt-6">
          <CsvUploader
            kind="wines"
            label="Importa catalogo vini (CSV)"
            hint="Colonne richieste: name, winery, vintage, price_per_bottle. Opzionali: denomination, grape, region, country, color, alcohol, image, description, external_sku, stock. Upsert idempotente per external_sku oppure (name + winery + vintage)."
          />
        </TabsContent>
        <TabsContent value="lots" className="mt-6">
          <CsvUploader
            kind="lots"
            label="Importa lotti B2B (CSV)"
            hint="Colonne richieste: product_type (wine/foodbox), title, quantity, price_per_unit. Opzionali: description, pallet_size, packaging, vintage, region, grape, location, available_until, seller_email, min_order_qty. I lotti importati da admin sono immediatamente approved."
          />
        </TabsContent>
        <TabsContent value="docs" className="mt-6">
          <DocumentUploader />
        </TabsContent>
        <TabsContent value="jobs" className="mt-6">
          <JobsHistory />
        </TabsContent>
      </Tabs>
    </div>
  );
}
